import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { AppDrawer } from '../ui/AppDrawer';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Warehouse, MapPin, Phone, User, Hash, Mail, Info, AlertTriangle, Unlock, Lock, PenLine } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { toast } from 'sonner';
import { warehouseAPI, poNotificationSettingsAPI } from '../../lib/api';

export const WarehouseDetailsModal = ({ 
  open, 
  onOpenChange, 
  warehouse, 
  onSaved 
}) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    phone: '',
    contact_name: '',
    po_sequence_start: 1000,
    po_sequence_padding: 4,
    po_received_email_to: '',
    po_received_email_cc: ''
  });
  
  const [currentSequence, setCurrentSequence] = useState(null);
  const [hasPoHistory, setHasPoHistory] = useState(false);
  const [overrideEnabled, setOverrideEnabled] = useState(false);
  const [showOverrideConfirm, setShowOverrideConfirm] = useState(false);
  const [tenantDefaults, setTenantDefaults] = useState({ to: '', cc: '', require_job_number: true });
  const [emailValidationError, setEmailValidationError] = useState(null);
  const [requireJobNumberOverride, setRequireJobNumberOverride] = useState(null);
  const [touched, setTouched] = useState(false);

  // Diagnostic logging for owners/admins when drawer opens
  useEffect(() => {
    if (!open || !warehouse) return;
    const timer = setTimeout(() => {
      try {
        const toInput = document.querySelector('[data-testid="warehouse-po-notification-to-input"]');
        if (toInput) {
          console.info('[WarehouseDrawer] Email field diagnostic', {
            userAgent: navigator.userAgent,
            field: 'po_received_email_to',
            disabled: toInput.disabled,
            readOnly: toInput.readOnly,
            pointerEvents: getComputedStyle(toInput).pointerEvents,
            opacity: getComputedStyle(toInput).opacity,
            parentFieldsetDisabled: !!toInput.closest('fieldset[disabled]')
          });

          const logInteraction = (e) => console.info(`[WarehouseDrawer] Email field ${e.type}`, { testId: e.target.dataset?.testid });
          toInput.addEventListener('focus', logInteraction);
          toInput.addEventListener('keypress', logInteraction);
          return () => {
            toInput.removeEventListener('focus', logInteraction);
            toInput.removeEventListener('keypress', logInteraction);
          };
        }
      } catch { /* diagnostic only */ }
    }, 500);
    return () => clearTimeout(timer);
  }, [open, warehouse]);

  useEffect(() => {
    if (open) {
      const loadTenantDefaults = async () => {
        try {
          const response = await poNotificationSettingsAPI.get();
          setTenantDefaults({
            to: response.data.po_received_email_to || '',
            cc: response.data.po_received_email_cc || '',
            require_job_number: response.data.require_job_number_for_po_received_email !== false
          });
        } catch (error) {
          console.error('Failed to load tenant notification defaults:', error);
        }
      };
      loadTenantDefaults();
    }
  }, [open]);

  useEffect(() => {
    if (warehouse && open) {
      setFormData({
        name: warehouse.name || '',
        code: warehouse.code || '',
        address_line1: warehouse.address_line1 || '',
        address_line2: warehouse.address_line2 || '',
        city: warehouse.city || '',
        state: warehouse.state || '',
        postal_code: warehouse.postal_code || '',
        phone: warehouse.phone || '',
        contact_name: warehouse.contact_name || '',
        po_sequence_start: warehouse.po_sequence_start || 1000,
        po_sequence_padding: warehouse.po_sequence_padding || 4,
        po_received_email_to: warehouse.po_received_email_to || '',
        po_received_email_cc: warehouse.po_received_email_cc || ''
      });
      const currSeq = warehouse.po_sequence_current ?? warehouse.po_sequence ?? null;
      setCurrentSequence(currSeq);
      setHasPoHistory(
        warehouse.has_po_history || 
        warehouse.po_sequence_current !== null || 
        (warehouse.po_sequence !== null && warehouse.po_sequence > 0)
      );
      setRequireJobNumberOverride(warehouse.require_job_number_for_po_received_email);
      setOverrideEnabled(false);
      setShowOverrideConfirm(false);
      setEmailValidationError(null);
      setTouched(false);
    }
  }, [warehouse, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setTouched(true);
    if (field === 'po_received_email_to' || field === 'po_received_email_cc') {
      setEmailValidationError(null);
    }
  };
  
  const isValidEmail = (email) => {
    if (!email || !email.trim()) return true;
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email.trim());
  };
  
  const validateEmailList = (emailString) => {
    if (!emailString || !emailString.trim()) return { valid: true, invalid: [] };
    const emails = emailString.split(',').map(e => e.trim()).filter(e => e);
    const invalid = emails.filter(e => !isValidEmail(e));
    return { valid: invalid.length === 0, invalid };
  };
  
  const handleOverrideLinkClick = useCallback(() => {
    setShowOverrideConfirm(true);
  }, []);
  
  const handleOverrideConfirm = useCallback(() => {
    setShowOverrideConfirm(false);
    setOverrideEnabled(true);
    toast.info('Starting Number field is now editable', {
      description: 'Remember: Stock & Load will skip existing PO numbers automatically.'
    });
  }, []);
  
  const handleOverrideCancel = useCallback(() => {
    setShowOverrideConfirm(false);
  }, []);
  
  const nextPoPreview = useMemo(() => {
    if (!formData.code) return null;
    let nextSeq;
    if (overrideEnabled) {
      nextSeq = parseInt(formData.po_sequence_start) || 1000;
    } else if (hasPoHistory && currentSequence !== null && currentSequence > 0) {
      nextSeq = currentSequence + 1;
    } else if (hasPoHistory && currentSequence === 0) {
      nextSeq = 1;
    } else {
      nextSeq = parseInt(formData.po_sequence_start) || 1000;
    }
    const padding = parseInt(formData.po_sequence_padding) || 4;
    return `${formData.code}${String(nextSeq).padStart(padding, '0')}`;
  }, [formData.code, formData.po_sequence_start, formData.po_sequence_padding, currentSequence, hasPoHistory, overrideEnabled]);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Warehouse name is required');
      return;
    }
    const toValidation = validateEmailList(formData.po_received_email_to);
    const ccValidation = validateEmailList(formData.po_received_email_cc);
    if (!toValidation.valid) {
      setEmailValidationError(`Invalid email(s) in To field: ${toValidation.invalid.join(', ')}`);
      return;
    }
    if (!ccValidation.valid) {
      setEmailValidationError(`Invalid email(s) in CC field: ${ccValidation.invalid.join(', ')}`);
      return;
    }

    setSaving(true);
    setEmailValidationError(null);
    try {
      const dataToSave = {
        ...formData,
        code: formData.code ? formData.code.toUpperCase() : null,
        po_sequence_start: parseInt(formData.po_sequence_start) || 1000,
        po_sequence_padding: parseInt(formData.po_sequence_padding) || 4,
        force_override: overrideEnabled,
        po_received_email_to: formData.po_received_email_to?.trim() || null,
        po_received_email_cc: formData.po_received_email_cc?.trim() || null,
        require_job_number_for_po_received_email: requireJobNumberOverride
      };
      await warehouseAPI.updateWarehouse(warehouse.id, dataToSave);
      toast.success('Warehouse updated', {
        description: overrideEnabled 
          ? 'Starting number updated. New POs will skip existing numbers automatically.'
          : 'Warehouse details have been saved.'
      });
      onSaved?.();
      onOpenChange(false);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save warehouse';
      if (message.includes('email')) {
        setEmailValidationError(message);
      }
      toast.error('Error', { description: message });
    } finally {
      setSaving(false);
    }
  };
  
  const isStartingNumberEditable = !hasPoHistory || overrideEnabled;

  return (
    <>
      <AppDrawer
        open={open}
        onOpenChange={onOpenChange}
        title={
          <span className="flex items-center gap-2">
            <Warehouse className="w-5 h-5" />
            Warehouse Details
          </span>
        }
        description="Edit warehouse settings, PO numbering, address, and notification emails."
        size="md"
        isDirty={touched}
        data-testid="warehouse-details-drawer"
        footer={
          <>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="warehouse-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-warehouse-button">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </>
        }
      >
        <div className="space-y-6">
          {/* Basic Info */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="wh-name">Warehouse Name *</Label>
              <Input id="wh-name" value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Main Warehouse" data-testid="warehouse-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="wh-code">PO Code (1-3 letters)</Label>
              <Input id="wh-code" value={formData.code}
                onChange={(e) => handleChange('code', e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3))}
                placeholder="DAP" maxLength={3} className="uppercase" data-testid="warehouse-code-input" />
              <p className="text-xs text-muted-foreground">Used for PO numbers (e.g., DAP2261)</p>
            </div>
          </div>
          
          {/* PO Numbering Configuration */}
          <div className="space-y-4 pt-4 border-t">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Hash className="w-4 h-4" /> PO Number Settings
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="wh-po-start" className={`flex items-center gap-1.5 ${!isStartingNumberEditable ? "text-muted-foreground" : ""}`}>
                  {!isStartingNumberEditable && <Lock className="w-3 h-3" />}
                  Starting Number
                  {!isStartingNumberEditable && (
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-xs text-muted-foreground font-normal cursor-help">(Locked)</span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          <p>This field is locked because this warehouse already has PO history. Use "Override lock" below to edit.</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </Label>
                <Input id="wh-po-start" type="number" min="1" max="999999"
                  value={formData.po_sequence_start}
                  onChange={(e) => handleChange('po_sequence_start', e.target.value)}
                  placeholder="1000" disabled={!isStartingNumberEditable}
                  className={!isStartingNumberEditable ? "opacity-60 cursor-not-allowed" : (overrideEnabled ? "border-amber-500 focus:ring-amber-500" : "")}
                  data-testid="warehouse-po-start-input" />
                {hasPoHistory && !overrideEnabled ? (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Locked — this warehouse already has PO history.</p>
                    <button type="button" onClick={handleOverrideLinkClick}
                      className="text-xs text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                      data-testid="override-lock-link">
                      <Unlock className="w-3 h-3" /> Override lock
                    </button>
                  </div>
                ) : overrideEnabled ? (
                  <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>Override active. PO numbers that already exist will be skipped automatically.</span>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">First PO starts at this number</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="wh-po-padding">Digit Padding</Label>
                <Select value={String(formData.po_sequence_padding)}
                  onValueChange={(v) => handleChange('po_sequence_padding', parseInt(v))}>
                  <SelectTrigger id="wh-po-padding" data-testid="warehouse-po-padding-select">
                    <SelectValue placeholder="Select padding" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 digits (001)</SelectItem>
                    <SelectItem value="4">4 digits (0001)</SelectItem>
                    <SelectItem value="5">5 digits (00001)</SelectItem>
                    <SelectItem value="6">6 digits (000001)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Zero-padding for sequence</p>
              </div>
            </div>
            {formData.code && (
              <div className={`rounded-lg p-3 border ${overrideEnabled ? 'bg-amber-50/50 border-amber-200' : 'bg-muted/50'}`}>
                <div className="text-xs text-muted-foreground mb-1">
                  {overrideEnabled ? 'Next PO will start at (may skip if exists):' : 'Next PO will be:'}
                </div>
                <div className="text-lg font-mono font-semibold text-primary" data-testid="po-preview">
                  {nextPoPreview}
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  Calculated as: {formData.code} + zero-padded sequence
                </div>
              </div>
            )}
          </div>

          {/* Shipping Address */}
          <div className="space-y-4 pt-4 border-t">
            <div className="flex items-center gap-2 text-sm font-medium">
              <MapPin className="w-4 h-4" /> Shipping Address (Ship-To for POs)
            </div>
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="wh-address1">Address Line 1</Label>
                <Input id="wh-address1" value={formData.address_line1}
                  onChange={(e) => handleChange('address_line1', e.target.value)}
                  placeholder="123 Warehouse Way" data-testid="warehouse-address1-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wh-address2">Address Line 2</Label>
                <Input id="wh-address2" value={formData.address_line2}
                  onChange={(e) => handleChange('address_line2', e.target.value)}
                  placeholder="Suite/Unit" data-testid="warehouse-address2-input" />
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="wh-city">City</Label>
                  <Input id="wh-city" value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    placeholder="City" data-testid="warehouse-city-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wh-state">State</Label>
                  <Input id="wh-state" value={formData.state}
                    onChange={(e) => handleChange('state', e.target.value)}
                    placeholder="State" data-testid="warehouse-state-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wh-zip">ZIP</Label>
                  <Input id="wh-zip" value={formData.postal_code}
                    onChange={(e) => handleChange('postal_code', e.target.value)}
                    placeholder="12345" data-testid="warehouse-zip-input" />
                </div>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="space-y-4 pt-4 border-t">
            <div className="flex items-center gap-2 text-sm font-medium">
              <User className="w-4 h-4" /> Contact Info (Optional)
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="wh-contact" className="flex items-center gap-1">
                  <User className="w-3 h-3" /> Contact Name
                </Label>
                <Input id="wh-contact" value={formData.contact_name}
                  onChange={(e) => handleChange('contact_name', e.target.value)}
                  placeholder="John Smith" data-testid="warehouse-contact-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wh-phone" className="flex items-center gap-1">
                  <Phone className="w-3 h-3" /> Phone
                </Label>
                <Input id="wh-phone" value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="(555) 123-4567" data-testid="warehouse-phone-input" />
              </div>
            </div>
          </div>
          
          {/* Warehouse Notification Emails */}
          <div className="space-y-4 pt-4 border-t">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Mail className="w-4 h-4" /> Warehouse Notification Emails
            </div>
            <p className="text-xs text-muted-foreground -mt-2 flex items-center gap-1">
              <PenLine className="w-3 h-3 flex-shrink-0" />
              Editable — used for PO notifications. Leave blank to use tenant defaults.
            </p>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="wh-po-email-to">Notification Email — To</Label>
                <Input id="wh-po-email-to" value={formData.po_received_email_to}
                  onChange={(e) => handleChange('po_received_email_to', e.target.value)}
                  placeholder={tenantDefaults.to || "Leave blank to use tenant default"}
                  data-testid="warehouse-po-notification-to-input" />
                {!formData.po_received_email_to && tenantDefaults.to && (
                  <p className="text-xs text-blue-600 flex items-center gap-1">
                    <Info className="w-3 h-3" /> Inherits tenant default: {tenantDefaults.to}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="wh-po-email-cc">Notification Email — CC</Label>
                <Input id="wh-po-email-cc" value={formData.po_received_email_cc}
                  onChange={(e) => handleChange('po_received_email_cc', e.target.value)}
                  placeholder={tenantDefaults.cc || "Leave blank to use tenant default"}
                  data-testid="warehouse-po-notification-cc-input" />
                {!formData.po_received_email_cc && tenantDefaults.cc && (
                  <p className="text-xs text-blue-600 flex items-center gap-1">
                    <Info className="w-3 h-3" /> Inherits tenant default: {tenantDefaults.cc}
                  </p>
                )}
              </div>
              {/* Job Number Requirement Override */}
              <div className="space-y-3 pt-2 border-t border-dashed">
                <Label className="text-sm font-medium">Job Number Requirement</Label>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input type="radio" id="job-inherit" name="job-requirement"
                      checked={requireJobNumberOverride === null}
                      onChange={() => { setRequireJobNumberOverride(null); setTouched(true); }}
                      className="w-4 h-4" data-testid="job-number-inherit-radio" />
                    <label htmlFor="job-inherit" className="text-sm cursor-pointer">
                      Inherit tenant setting
                      <span className="text-xs text-muted-foreground ml-1">
                        (currently: {tenantDefaults.require_job_number ? 'Required' : 'Not required'})
                      </span>
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="radio" id="job-required" name="job-requirement"
                      checked={requireJobNumberOverride === true}
                      onChange={() => { setRequireJobNumberOverride(true); setTouched(true); }}
                      className="w-4 h-4" data-testid="job-number-required-radio" />
                    <label htmlFor="job-required" className="text-sm cursor-pointer">
                      Override: Require job number
                      <span className="text-xs text-muted-foreground ml-1">(emails only sent when job number exists)</span>
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="radio" id="job-not-required" name="job-requirement"
                      checked={requireJobNumberOverride === false}
                      onChange={() => { setRequireJobNumberOverride(false); setTouched(true); }}
                      className="w-4 h-4" data-testid="job-number-not-required-radio" />
                    <label htmlFor="job-not-required" className="text-sm cursor-pointer">
                      Override: Don't require job number
                      <span className="text-xs text-muted-foreground ml-1">(emails sent for all POs)</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            {emailValidationError && (
              <div className="p-2 bg-destructive/10 border border-destructive/30 rounded text-sm text-destructive">
                {emailValidationError}
              </div>
            )}
          </div>
        </div>
      </AppDrawer>

      {/* Override Confirmation - stays as centered Dialog */}
      <Dialog open={showOverrideConfirm} onOpenChange={setShowOverrideConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Override PO Number Lock?
            </DialogTitle>
            <DialogDescription asChild>
              <div className="text-left space-y-3 pt-2">
                <p>This warehouse has existing PO history. Changing the starting number can cause duplicates or gaps.</p>
                <p className="font-medium">Stock & Load will automatically skip PO numbers that already exist, but numbering may not be perfectly sequential.</p>
                <p className="text-muted-foreground">This affects all future POs for this warehouse.</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-end gap-2">
            <Button variant="outline" onClick={handleOverrideCancel} data-testid="override-cancel-button">Cancel</Button>
            <Button onClick={handleOverrideConfirm} className="bg-amber-600 hover:bg-amber-700" data-testid="override-confirm-button">
              I Understand — Enable Editing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WarehouseDetailsModal;
